import React from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";

// Import existing images
import dashboardImg from "../assets/img/dashboard.png";
import joblogImg from "../assets/img/joblog.png";
import routesImg from "../assets/img/routes.png";
import responsiveImg from "../assets/img/ResponsiveDesign.jpg";
import laptopImg from "../assets/img/laptop.png";
import consumer from "../assets/img/consumer.jpg";
import consumerdetail from "../assets/img/consumer_detail.jpg";
import joblog from "../assets/img/joblog.jpg";
import mileagelog from "../assets/img/mileagelog.jpg";
import report from "../assets/img/report.jpg";

function Home() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-dark text-white py-5">
        <div className="container">
          <div className="row align-items-center min-vh-75">
            <div className="col-lg-6">
              <div className="hero-content">
                <h1 className="display-6 fw-bold text-white mb-4">
                  Stop Drowning in Paperwork
                  <span className="text-warning d-block fs-4">
                    Start Running a Professional Operation
                  </span>
                </h1>
                <p className="lead text-light mb-4">
                  Finally, a system that works the way you do - built for people
                  actually doing the job. Transform your CRP program from chaos
                  to professional excellence.
                </p>
                <div className="hero-features mb-4">
                  <div className="d-flex align-items-center mb-2">
                    <i className="bi bi-check-circle-fill text-warning me-2"></i>
                    <span className="text-light">
                      Save 20+ hours per month on paperwork
                    </span>
                  </div>
                  <div className="d-flex align-items-center mb-2">
                    <i className="bi bi-check-circle-fill text-warning me-2"></i>
                    <span className="text-light">
                      Job coaches love the mobile app
                    </span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-check-circle-fill text-warning me-2"></i>
                    <span className="text-light">
                      Impress customers with professional reports
                    </span>
                  </div>
                </div>
                <div className="hero-cta">
                  <Link to="/demo" className="btn btn-warning btn-lg me-3">
                    <i className="bi bi-calendar-check me-2"></i>
                    See It In Action
                  </Link>
                  <Link
                    to="/solutions"
                    className="btn btn-outline-light btn-lg"
                  >
                    Learn How It Works
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="hero-image animate-float">
                <img
                  src={dashboardImg}
                  alt="WebVenly Dashboard"
                  className="img-fluid rounded-3 shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Pain Points & Solutions Section */}
      <section className="py-5 bg-gradient-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold mb-3">
              We Know What You're Going Through
            </h2>
            <p className="lead text-muted">
              These daily struggles are killing your productivity and driving
              you crazy
            </p>
          </div>

          <div className="row g-4">
            {[
              {
                icon: "📝",
                iconClass: "bi bi-file-earmark-text",
                title: "Handwritten Logs Are a Nightmare",
                pain: "Job coaches submit unprofessional, illegible handwritten logs that you have to decipher and retype. You spend more time trying to figure out what happened than actually helping consumers.",
                solution: "Data Entry in the Field",
                description:
                  "Job coaches log services directly from their mobile or tablet device in under 2 minutes. Clean, professional documentation with Signatures to submit to Customers.",
                color: "danger",
              },
              {
                icon: "📊",
                iconClass: "bi bi-graph-down",
                title: "Monthly Reports Take Forever",
                pain: "You spend days every month compiling scattered information into reports. It's a nightmare of spreadsheets, lost notes, and constant back-and-forth with job coaches.",
                solution: "Automated Professional Reports",
                description:
                  "Professional, standardized progress reports generated automatically for every consumer. What used to take days now takes minutes.",
                color: "warning",
              },
              {
                icon: "⏰",
                iconClass: "bi bi-alarm",
                title: "You're Always Worried About Authorizations",
                pain: "You never know if you're about to exceed authorized (hours/miles) until it's too late. Billing disputes and angry customers are just part of life.",
                solution: "Real-Time Authorization Tracking",
                description:
                  "Know exactly how many hours/miles are authorized, used, and remaining for every consumer. Never accidentally exceed approved authorizations again.",
                color: "success",
              },
              {
                icon: "💸",
                iconClass: "bi bi-currency-dollar",
                title: "Billing Is a Mess",
                pain: "Creating invoices means digging through files, calculating mileage by hand, and praying you didn't miss anything. Getting paid takes forever.",
                solution: "Professional Billing Documentation",
                description:
                  "Generate detailed supporting documentation in minutes to submit with your invoice. Professional presentation gets you paid faster.",
                color: "primary",
              },
            ].map((item, idx) => (
              <div className="col-lg-6" key={idx}>
                <div className="pain-solution-card h-100 p-4 rounded-3 bg-white shadow-hover">
                  <div className="d-flex align-items-start mb-3">
                    <div
                      className={`pain-icon bg-${item.color} bg-opacity-10 rounded-circle p-3 me-3`}
                    >
                      <i
                        className={`${item.iconClass} text-${item.color} fs-4`}
                      ></i>
                    </div>
                    <div className="flex-grow-1">
                      <h4 className="fw-bold mb-2">{item.title}</h4>
                      <p className="text-muted small mb-3 fst-italic">
                        "{item.pain}"
                      </p>
                    </div>
                  </div>
                  <div
                    className={`solution-badge bg-${item.color} text-white px-3 py-2 rounded-pill d-inline-block mb-3`}
                  >
                    <small className="fw-semibold">✓ {item.solution}</small>
                  </div>
                  <p className="mb-0">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Job Coaches Section */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold mb-3">
              Your Job Coaches Will Actually Want to Use This
            </h2>
            <p className="lead text-muted">
              No more fighting with your team about documentation. They'll love
              how fast and easy it is.
            </p>
          </div>
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h2 className="display-8 fw-bold mb-4">
                Work Smarter not Harder!
              </h2>
              <p className="lead mb-4">Notice the Change from Day One.</p>
              <div className="coach-benefits">
                <div className="d-flex align-items-start mb-3">
                  <div className="benefit-icon bg-success bg-opacity-10 rounded-circle p-2 me-3">
                    <i className="bi bi-phone text-success"></i>
                  </div>
                  <div>
                    <h6 className="fw-semibold mb-1">Quick Data Entry</h6>
                    <small className="text-muted">
                      Right from their phone, while they're still with the
                      consumer.
                    </small>
                  </div>
                </div>
                <div className="d-flex align-items-start mb-3">
                  <div className="benefit-icon bg-success bg-opacity-10 rounded-circle p-2 me-3">
                    <i className="bi bi-clock text-success"></i>
                  </div>
                  <div>
                    <h6 className="fw-semibold mb-1">
                      No More Evening Paperwork
                    </h6>
                    <small className="text-muted">
                      Everything gets documented during work hours, not at home.
                    </small>
                  </div>
                </div>
                <div className="d-flex align-items-start mb-4">
                  <div className="benefit-icon bg-success bg-opacity-10 rounded-circle p-2 me-3">
                    <i className="bi bi-shield-check text-success"></i>
                  </div>
                  <div>
                    <h6 className="fw-semibold mb-1">
                      Never Lose Documentation Again
                    </h6>
                    <small className="text-muted">
                      No more lost scraps of paper or forgotten notes.
                    </small>
                  </div>
                </div>
                <div className="d-flex align-items-start mb-4">
                  <div className="benefit-icon bg-success bg-opacity-10 rounded-circle p-2 me-3">
                    <i className="bi bi-book text-success"></i>
                  </div>
                  <div>
                    <h6 className="fw-semibold mb-1">Quick Access</h6>
                    <small className="text-muted">
                      View information on Client and Customer quickly.
                    </small>
                  </div>
                </div>
                <div className="d-flex align-items-start mb-4">
                  <div className="benefit-icon bg-success bg-opacity-10 rounded-circle p-2 me-3">
                    <i className="bi bi-laptop text-success"></i>
                  </div>
                  <div>
                    <h6 className="fw-semibold mb-1">Responsive</h6>
                    <small className="text-muted">
                      Can access via Desktop, Laptop, Tablet or Mobile Device.
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              {/* Bootstrap Carousel */}
              <div className="coach-showcase position-relative">
                <div
                  id="jobCoachCarousel"
                  className="carousel slide shadow-md rounded-3"
                  data-bs-ride="carousel"
                  data-bs-interval="4000"
                >
                  <div className="carousel-indicators">
                    <button
                      type="button"
                      data-bs-target="#jobCoachCarousel"
                      data-bs-slide-to="0"
                      className="active"
                    ></button>
                    <button
                      type="button"
                      data-bs-target="#jobCoachCarousel"
                      data-bs-slide-to="1"
                    ></button>
                    <button
                      type="button"
                      data-bs-target="#jobCoachCarousel"
                      data-bs-slide-to="2"
                    ></button>
                    <button
                      type="button"
                      data-bs-target="#jobCoachCarousel"
                      data-bs-slide-to="3"
                    ></button>
                    <button
                      type="button"
                      data-bs-target="#jobCoachCarousel"
                      data-bs-slide-to="4"
                    ></button>
                  </div>

                  <div className="carousel-inner rounded-3">
                    <div className="carousel-item active text-center">
                      <img
                        src={joblog}
                        className="img-fluid rounded-3"
                        alt="Mobile Job Logging"
                        style={{ maxWidth: "50%", height: "auto" }}
                      />
                      <div className="carousel-caption d-none d-md-block bg-dark bg-opacity-75 rounded p-2">
                        <h5>Mobile Job Logging</h5>
                        <p>Service Logged in under 2 minutes</p>
                      </div>
                    </div>

                    <div className="carousel-item text-center">
                      <img
                        src={mileagelog}
                        className="img-fluid rounded-3"
                        alt="Admin Dashboard"
                        style={{ maxWidth: "50%", height: "auto" }}
                      />
                      <div className="carousel-caption d-none d-md-block bg-dark bg-opacity-75 rounded p-2">
                        <h5>Mileage Routes</h5>
                        <p>Quick and Easy Mileage log with Google support</p>
                      </div>
                    </div>

                    <div className="carousel-item text-center">
                      <img
                        src={consumer}
                        className="img-fluid rounded-3"
                        alt="GPS Mileage Tracking"
                        style={{ maxWidth: "50%", height: "auto" }}
                      />
                      <div className="carousel-caption d-none d-md-block bg-dark bg-opacity-75 rounded p-2">
                        <h5>At A Glance</h5>
                        <p>Always know hours/miles used and available. </p>
                      </div>
                    </div>

                    <div className="carousel-item text-center">
                      <img
                        src={consumerdetail}
                        className="img-fluid rounded-3"
                        alt="Mobile Responsive"
                        style={{ maxWidth: "50%", height: "auto" }}
                      />
                      <div className="carousel-caption d-none d-md-block bg-dark bg-opacity-75 rounded p-2">
                        <h5>Works Anywhere</h5>
                        <p>Phone, tablet, or desktop</p>
                      </div>
                    </div>

                    <div className="carousel-item text-center">
                      <img
                        src={report}
                        className="img-fluid rounded-3"
                        alt="Professional Operation"
                        style={{ maxWidth: "50%", height: "auto" }}
                      />
                      <div className="carousel-caption d-none d-md-block bg-dark bg-opacity-75 rounded p-2">
                        <h5>Professional Reports</h5>
                        <p>Impress your customers</p>
                      </div>
                    </div>
                  </div>

                  <button
                    className="carousel-control-prev"
                    type="button"
                    data-bs-target="#jobCoachCarousel"
                    data-bs-slide="prev"
                  >
                    <span className="carousel-control-prev-icon"></span>
                    <span className="visually-hidden">Previous</span>
                  </button>
                  <button
                    className="carousel-control-next"
                    type="button"
                    data-bs-target="#jobCoachCarousel"
                    data-bs-slide="next"
                  >
                    <span className="carousel-control-next-icon"></span>
                    <span className="visually-hidden">Next</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Timeline */}
      <section className="py-5 bg-gradient-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold mb-3">See Results Fast</h2>
            <p className="lead text-muted">
              You'll notice the difference immediately, and it gets better every
              month!
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="roi-card text-center p-4 rounded-3 bg-white shadow-hover h-100">
                <div
                  className="roi-timeline-badge bg-primary text-white rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{ width: "60px", height: "60px" }}
                >
                  <span className="fw-bold">
                    1st
                    <br />
                    Week
                  </span>
                </div>
                <h5 className="fw-bold mb-3">Immediate Relief</h5>
                <ul className="list-unstyled text-start">
                  <li className="d-flex align-items-start mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Job coaches start logging on mobile or laptop</span>
                  </li>
                  <li className="d-flex align-items-start mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>No more handwritten forms to decipher</span>
                  </li>
                  <li className="d-flex align-items-start">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Clean data for payroll and month end</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="col-md-4">
              <div className="roi-card text-center p-4 rounded-3 bg-white shadow-hover h-100">
                <div
                  className="roi-timeline-badge bg-success text-white rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{ width: "60px", height: "60px" }}
                >
                  <span className="fw-bold">
                    1st
                    <br />
                    Month
                  </span>
                </div>
                <h5 className="fw-bold mb-3">Major Time Savings</h5>
                <ul className="list-unstyled text-start">
                  <li className="d-flex align-items-start mb-2">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span>Save 20+ hours on monthly billing reports</span>
                  </li>
                  <li className="d-flex align-items-start mb-2">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span>Eliminates errors and disputes</span>
                  </li>
                  <li className="d-flex align-items-start">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <span>Customers notice the improvement</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="col-md-4">
              <div className="roi-card text-center p-4 rounded-3 bg-white shadow-hover h-100">
                <div
                  className="roi-timeline-badge bg-warning text-white rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{ width: "60px", height: "60px" }}
                >
                  <span className="fw-bold">
                    6th
                    <br />
                    Month
                  </span>
                </div>
                <h5 className="fw-bold mb-3">Competitive Advantage</h5>
                <ul className="list-unstyled text-start">
                  <li className="d-flex align-items-start mb-2">
                    <i className="bi bi-check-circle-fill text-warning me-2 mt-1"></i>
                    <span>Win contract renewals easily</span>
                  </li>
                  <li className="d-flex align-items-start mb-2">
                    <i className="bi bi-check-circle-fill text-warning me-2 mt-1"></i>
                    <span>Reputation as professional operation</span>
                  </li>
                  <li className="d-flex align-items-start">
                    <i className="bi bi-check-circle-fill text-warning me-2 mt-1"></i>
                    <span>Answer Customer Question in minutes</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Key Features with Real Benefits */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold mb-3">
              Everything You Need to Run a Professional Operation
            </h2>
            <p className="lead text-muted">
              Built for the real world, not some corporate fantasy
            </p>
          </div>

          <div className="row g-4">
            {[
              {
                icon: "📱",
                title: "Mobile-First Job Logging",
                desc: "Job coaches document services in under 2 minutes, right from their phone, laptop or tablet. No more lost notes or evening paperwork sessions.",
                realBenefit: "Your team will actually want to use it",
              },
              {
                icon: "🚗",
                title: "Google Maps, Mileage Tracking",
                desc: "Mileage calculation between consumer visits. No more guesswork or manual calculations that cause billing disputes.",
                realBenefit: "Get paid faster with accurate documentation",
              },
              {
                icon: "📊",
                title: "View payroll in a snap",
                desc: "Review weekly, by-weekly or monthly hours and mileage that will provide totals.",
                realBenefit: "Spend time growing your business",
              },
              {
                icon: "⏰",
                title: "Authorization Tracking",
                desc: "Real-time tracking of approved hours with alerts before you exceed limits. Never surprise a customer with overruns again.",
                realBenefit: "Sleep better knowing you're always compliant",
              },
              {
                icon: "💰",
                title: "Professional Looking Documentation",
                desc: "Generate detailed supporting documentation in minutes. Clean presentation gets you paid faster.",
                realBenefit: "Improve cash flow with faster payments",
              },
              {
                icon: "🔐",
                title: "Built-in Compliance",
                desc: "Automatic audit trails and documentation standards. Ready for any state review or customer inquiry.",
                realBenefit: "Stop worrying about compliance issues",
              },
            ].map((feature, idx) => (
              <div className="col-md-4" key={idx}>
                <div className="feature-card text-center h-100 p-4 rounded-3 bg-white shadow-hover position-relative overflow-hidden">
                  <div className="feature-gradient bg-gradient-primary"></div>
                  <div className="position-relative">
                    <div className="feature-icon mb-3">
                      <span className="display-4">{feature.icon}</span>
                    </div>
                    <h5 className="fw-bold mb-3">{feature.title}</h5>
                    <p className="text-muted mb-3">{feature.desc}</p>
                    <div className="real-benefit bg-primary bg-opacity-10 text-primary px-3 py-2 rounded-pill">
                      <small className="fw-semibold">
                        {feature.realBenefit}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories - Dual Testimonials */}
      <section className="py-5 bg-gradient-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold mb-3">What Our Clients Say</h2>
            <p className="lead text-muted">Real results from real businesses</p>
          </div>

          <div className="row g-4">
            {/* David Wyatt Testimonial */}
            <div className="col-lg-12">
              <div className="success-story bg-white rounded-3 shadow-lg p-4 h-100 d-flex flex-column">
                <div className="success-icon mb-3 text-center">
                  <div
                    className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center"
                    style={{ width: "60px", height: "60px" }}
                  >
                    <i className="bi bi-trophy text-primary fs-3"></i>
                  </div>
                </div>
                <blockquote className="blockquote mb-4 flex-grow-1">
                  <p className="mb-0">
                    "Before WebVenly, we processed all of our baseline services
                    manually via spreadsheets and other tracking forms. Once we
                    neared 100 clients and 12+ employees, this became all but
                    impossible and incredibly time-consuming, especially during
                    monthly billings. We also experienced myriad issues with
                    client hour and mileage tracking, costing us time and money.
                  </p>
                  <p className="mt-3 mb-0">
                    Once we implemented the WebVenly software platform, our
                    efficiency increased ten-fold, and made everything from
                    client billing, hour/mileage tracking, and even payroll
                    hours a breeze. The software is extremely easy to use, and
                    all of our employees now use it exclusively in their
                    day-to-day work with our clients. Many aspects of the
                    software were created to meet our specific needs, and the
                    platform itself is very flexible.{" "}
                    <strong>
                      I cannot recommend WebVenly enough to any company looking
                      to increase productivity and decrease stress!"
                    </strong>
                  </p>
                </blockquote>
                <footer className="blockquote-footer mt-auto">
                  <strong>David Wyatt</strong>
                  <cite title="Source Title" className="d-block">
                    Co-Founder, Mach 1 CRP Services, LLC
                  </cite>
                </footer>
                <div className="success-metrics mt-3">
                  <div className="row text-center">
                    <div className="col-6">
                      <div className="metric-number text-primary fw-bold fs-4">
                        10x
                      </div>
                      <div className="metric-label small text-muted">
                        Efficiency Increase
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="metric-number text-primary fw-bold fs-4">
                        100+
                      </div>
                      <div className="metric-label small text-muted">
                        Clients Managed
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section bg-gradient-primary text-white py-5">
        <div className="container text-center">
          <h2 className="display-5 fw-bold mb-3">
            Ready to Stop Fighting Paperwork?
          </h2>
          <p className="lead mb-4">
            Transform your operation from chaos to professional excellence. Your
            customers will notice the difference, your job coaches will thank
            you, and you'll wonder why you waited so long.
          </p>
          <div className="cta-buttons">
            <Link to="/demo" className="btn btn-light btn-lg me-3">
              <i className="bi bi-play-circle me-2"></i>
              See It in Action
            </Link>
            <Link to="/contact" className="btn btn-outline-light btn-lg">
              <i className="bi bi-chat-dots me-2"></i>
              Talk to a Real Person
            </Link>
          </div>
          <p className="mt-4 mb-0 text-white-75">
            <small>
              No corporate nonsense • Built by people who understand CRP work •
              Used by vendors who actually get results
            </small>
          </p>
        </div>
      </section>
    </Layout>
  );
}

export default Home;
